import { useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { GeoJSON, Marker, Pane, Polyline, Tooltip } from "react-leaflet";
import bezierSpline from "@turf/bezier-spline";
import { lineString } from "@turf/helpers";
import { divIcon, DomEvent } from "leaflet";
import hash from "object-hash";

import { Card, Stack, Typography } from "@mui/material";

import { MAP_CONTENTS_OPACITY, ROUTE_SIZE, STATIONS_COLOR } from "Configs/map";
import { IPath } from "Types/path";
import { IStation } from "Types/station";

import useEditPath from "Hooks/useEditPath";
import usePaths from "Hooks/usePaths";
import { useAppDispatch, useAppSelector } from "Hooks/useRedux";

import { changeModal } from "Redux/modalSlice";
import { changeLargeIconByClick } from "Redux/mapSlice";
import { setTooltip } from "Redux/tooltipSlice";
import { selectStationStar } from "Redux/stationsSlice";

import HintBox from "Components/HintBox";
import { Box } from "@mui/system";
import { STATIONS2 } from "Constants/Stations";
import zIndex from "@mui/material/styles/zIndex";
import DayPicker from "Components/DayPicker";
import { changeShowPaths, changeShowReportRightControl } from "Redux/sideSlice";

const ICON_SIZE = 22;

export default function ArcAndShadow() {
  const { name, type } = useAppSelector((state) => state.operation);
  const { visible, pathShowType, deselectedPathes } = useAppSelector((state) => state.services);
  const { visibility } = useAppSelector((state) => state.filters);
  const { pathLayer } = useAppSelector((state) => state.layers);
  const { path: selectedPath } = useAppSelector((state) => state.app);
  const { visibleType, singlePathData } = useAppSelector((state) => state.singleLocation);
  const { starStation } = useAppSelector((state) => state.stations);
  const { largeIconByClick } = useAppSelector((state) => state.map);

  const dispatch = useAppDispatch();
  const { editPath } = useEditPath();

  const [hoveredLine, setHoveredLine] = useState<null | number>(null);
  const [showHint, setShowHint] = useState<boolean>(false);
  const [largeIconByHover, setLargeIconByHover] = useState<number>(0);
  const [largeDestination, setLargeDestination] = useState<number>(0);

  const { data } = usePaths(20);

  const handleSelectedStation = (station: any) => {
    if (starStation.id === station.id) {
      dispatch(selectStationStar({ starStation: {}, visible: false }));
    } else {
      dispatch(selectStationStar({ starStation: station, visible: true }));
    }
  };

  const simplePolyline = useMemo(() => {
    let finalData: IPath[] | undefined = [];
    if (visible) {
      finalData = data?.filter((path) => path?.type === type);
    } else if (visibleType === "path") {
      finalData = singlePathData;
    }
    return finalData?.map((path) => {
      if (!path) return null;
      if (deselectedPathes.includes(path.id)) return;

      const { service } = path;
      const pathStations = path?.stations;
      const stations: IStation[][] = [];
      for (let i = 0; i + 1 < pathStations.length; i += 1) {
        stations.push(pathStations.slice(i, i + 2));
      }

      return stations.map((item, index) => {
        const positions: [number, number][] = item.map((i) => [i.lat, i.lng]);
        const routeColor: string = STATIONS_COLOR[item[0].type];

        return (
          <>
            <Polyline
              positions={positions}
              pathOptions={{
                color: routeColor,
                weight: ROUTE_SIZE,
                opacity: hoveredLine !== null && hoveredLine !== index ? 0.3 : 1,
              }}
            />
            <Polyline
              positions={positions}
              pathOptions={{ color: "transparent", weight: ROUTE_SIZE * 3 }}
              eventHandlers={{
                click: (e) => {
                  DomEvent.stopPropagation(e);

                  dispatch(
                    changeModal({
                      status: "station-timing",
                      data: { serviceId: service.id, stationId: item[0].id, workingDays: service.working_days },
                    })
                  );
                },
                mouseover: (e) => {
                  setHoveredLine(index);
                  setShowHint(true);
                  dispatch(
                    setTooltip({
                      text: `مسیر ${service.path_detail?.name} - ${pathStations.length} ایستگاه - کد ${
                        service.path_detail?.code || "نامشخص"
                      }`,
                    })
                  );
                },
                mouseout: () => {
                  setShowHint(false);
                  setHoveredLine(null);
                  dispatch(setTooltip({ text: "" }));
                },
              }}
            />
          </>
        );
      });
    });
  }, [data, hoveredLine, type, visibleType, singlePathData, deselectedPathes]);
  const curvePolyline = useMemo(() => {
    if (!data && singlePathData.length === 0) return null;

    const filteredData = visibility.length > 0 ? data?.filter((path: IPath) => visibility.includes(path?.type)) : data;
    let finalData: IPath[] = [];
    if (visible && selectedPath?.id) {
      finalData = [selectedPath];
    } else if (filteredData && visible) {
      finalData = filteredData;
    } else if (visibleType === "path") {
      finalData = singlePathData;
    }

    return finalData?.map((path) => {
      if (deselectedPathes.includes(path.id)) return;
      const { service } = path;
      if (!path || path.stations.length === 0) return null;
      const { stations } = path;
      const sourceStation = stations[0];
      const destinationStation = stations[stations.length - 1];

      const serviceColor = STATIONS_COLOR[path.type];
      const stationData = STATIONS2.filter((i) => i.type === path.type)[0];
      const sourceIconMarkup = renderToStaticMarkup(
        <Box
          style={{
            width: "40px",
            height: "40px",
            transform:
              largeIconByClick === sourceStation.id || largeIconByHover === sourceStation.id
                ? "scale( 1.8 , 1.8 )"
                : "scale( 1 , 1 )",
            backgroundColor: "#fff",
            borderRadius: "50%",
            border: "2px solid",
            display: "grid",
            placeItems: "center",
            borderColor: serviceColor,
          }}
        >
          {stationData.icon}
        </Box>
      );

      const destinationIconMarkup = renderToStaticMarkup(
        <Box
          style={{
            width: "49px",
            height: "53px",
            backgroundColor: "#ffffff00",
            borderRadius: "none",
            display: "grid",
            placeItems: "center",
            borderColor: serviceColor,
            marginLeft: "6px",
            marginTop: "2px",
            transform:
              largeIconByClick === destinationStation.id || largeIconByHover === destinationStation.id
                ? "scale( 1.8 , 1.8 )"
                : "scale( 1 , 1 )",
          }}
        >
          <svg width="55" height="50" viewBox="4 2 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7.96891 4.24999C9.31606 1.91666 12.6839 1.91666 14.0311 4.25L18.3612 11.75C19.7084 14.0833 18.0244 17 15.3301 17H6.66986C3.97557 17 2.29163 14.0833 3.63878 11.75L7.96891 4.24999Z"
              fill="#fff"
              stroke={serviceColor}
              strokeWidth="1"
              strokeLinejoin="round"
            />
          </svg>
          {stationData.icon}
        </Box>
      );

      const sourceIcon = divIcon({
        html: sourceIconMarkup,
        iconSize: [ICON_SIZE, ICON_SIZE],
        iconAnchor: [18, 30],
      });
      const destinationIcon = divIcon({
        html: destinationIconMarkup,
        iconSize: [ICON_SIZE, ICON_SIZE],
        iconAnchor: [18, 30],
      });

      const finalResult = [];

      const latLng1 = [sourceStation.lat, sourceStation.lng];
      const latLng2 = [destinationStation.lat, destinationStation.lng];

      const offsetX = latLng2[1] - latLng1[1];
      const offsetY = latLng2[0] - latLng1[0];

      const r1 = Math.sqrt(offsetX ** 2 + offsetY ** 2);
      const theta = Math.atan2(offsetY, offsetX);

      const thetaOffset = 3.14 / 20;

      const r2 = r1 / 2 / Math.cos(thetaOffset);
      const theta2 = theta + thetaOffset;

      const midpointX = r2 * Math.cos(theta2) + latLng1[1];
      const midpointY = r2 * Math.sin(theta2) + latLng1[0];

      const midpointLatLng = [midpointY, midpointX];

      finalResult.push(latLng1, midpointLatLng, latLng2);

      const line = lineString(finalResult.map((latLng) => [latLng[1], latLng[0]]));
      const curved = bezierSpline(line, { sharpness: 1, resolution: 10000 });

      const events = {
        click: () => {
          dispatch(changeShowReportRightControl(true));
          dispatch(changeShowPaths(false));
          editPath(path);
        },
      };

      const isActive = name === "create-path" || name === "edit-path";

      const tooltipContent = !isActive && (
        <Tooltip direction="top" offset={[0, -20]}>
          <Card sx={{ py: 2, px: 3, direction: "ltr", width: "300px" }} className="fa-number">
            <Stack alignItems="center">
              <Typography>{path.name}</Typography>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography sx={{ m: "0 !important" }}>نوع مسیر:</Typography>
                <Typography sx={{ m: "0 !important" }}>{stationData.icon}</Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography sx={{ m: "0 !important" }}>تعداد ایستگاه:</Typography>
                <Typography sx={{ m: "0 !important" }}>{stations.length}</Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%", mt: "10px" }}>
                <Typography sx={{ m: "0 !important" }}>کد مسیر</Typography>
                <Typography sx={{ m: "0 !important" }}>{path.code}</Typography>
              </Stack>
              <Stack sx={{ mt: "10px" }}>
                <DayPicker
                  label="روزهای فعالیت"
                  multiple
                  padding="1px"
                  minWidth={35}
                  isGeneral={true}
                  value={path?.service?.working_days || []}
                  setData={(value) => {}}
                />
              </Stack>
            </Stack>
          </Card>
        </Tooltip>
      );

      const stationLength = Number(path?.stations?.length) - 1;

      const sourceTooltip = !isActive && (
        <Tooltip
          direction="top"
          // permanent={largeSourceIconByClick === sourceStation.id}
          // permanent={true}
          // opacity={0.5}
          key={`${sourceStation.id}_${largeIconByHover}`}
          offset={largeIconByClick === sourceStation.id || largeIconByHover === sourceStation.id ? [15, -20] : [0, -20]}
        >
          <Card sx={{ py: 2, px: 3, direction: "ltr", width: "300px" }}>
            <Stack alignItems="center">
              <Typography>{sourceStation.title}</Typography>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  کد ایستگاه :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {sourceStation.code}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  عرض جغرافیایی :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {sourceStation.lat}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  طول جغرافیایی :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {sourceStation.lng}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Tooltip>
      );

      const destinationTooltip = !isActive && (
        <Tooltip direction="top" offset={[0, -20]}>
          <Card sx={{ py: 2, px: 3, direction: "ltr", width: "300px" }}>
            <Stack alignItems="center">
              <Typography>{destinationStation.title}</Typography>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  کد ایستگاه :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {destinationStation.code}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  عرض جغرافیایی :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {destinationStation.lat}
                </Typography>
              </Stack>
              <Stack direction="row" sx={{ justifyContent: "space-between", width: "100%" }}>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  طول جغرافیایی :
                </Typography>
                <Typography className="fa-number" sx={{ m: "0 0 7px 0 !important" }}>
                  {destinationStation.lng}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Tooltip>
      );

      const opacity = isActive ? MAP_CONTENTS_OPACITY : 1;

      return (
        <>
          {(pathShowType.includes(3) ||
            (visibleType === "path" && !((pathShowType.includes(1) || pathShowType.includes(5)) && visible))) && (
            <>
              <Marker
                key={`source-${path.id}`}
                position={[sourceStation.lat, sourceStation.lng]}
                icon={sourceIcon}
                eventHandlers={{
                  click: () => {
                    handleSelectedStation(sourceStation);
                    dispatch(changeLargeIconByClick(sourceStation.id));
                    setLargeIconByHover(0);
                  },
                  mouseover: () => {
                    setLargeIconByHover(sourceStation.id);
                  },
                  mouseout: () => {
                    setLargeIconByHover(0);
                  },
                }}
                // opacity={opacity}
                zIndexOffset={1000}
              >
                {sourceTooltip}
              </Marker>
              <Marker
                key={`destination-${path.id}`}
                position={[destinationStation.lat, destinationStation.lng]}
                icon={destinationIcon}
                eventHandlers={{
                  click: () => {
                    handleSelectedStation(destinationStation);
                    setLargeDestination(destinationStation.id);
                  },
                  mouseover: () => {
                    setLargeIconByHover(destinationStation.id);
                  },
                  mouseout: () => {
                    setLargeIconByHover(0);
                  },
                }}
                // opacity={opacity}
                zIndexOffset={1000}
              >
                {destinationTooltip}
              </Marker>
            </>
          )}

          {(pathShowType.includes(4) ||
            (visibleType === "path" && !((pathShowType.includes(1) || pathShowType.includes(5)) && visible))) && (
            <Polyline
              positions={[
                [sourceStation.lat, sourceStation.lng],
                [destinationStation.lat, destinationStation.lng],
              ]}
              pathOptions={{ opacity: 0.2, color: "#000", weight: 5 }}
              eventHandlers={events}
              // opacity={opacity}
            >
              {tooltipContent}
            </Polyline>
          )}
          {(pathShowType.includes(3) ||
            (visibleType === "path" && !((pathShowType.includes(1) || pathShowType.includes(5)) && visible))) && (
            <GeoJSON data={curved} pathOptions={{ weight: 5, color: serviceColor }} eventHandlers={events} key={hash(curved)}>
              {tooltipContent}
            </GeoJSON>
          )}
        </>
      );
    });
  }, [
    data,
    visibility,
    name,
    pathShowType,
    visibleType,
    visible,
    singlePathData,
    deselectedPathes,
    largeIconByClick,
    largeIconByHover,
    largeDestination,
  ]);

  return (
    <>
      <Pane name="path-arc" style={{ zIndex: pathLayer }} key={pathLayer}>
        {curvePolyline}
      </Pane>

      {/* {name === "station-timing" && simplePolyline} */}

      <HintBox visible={showHint}>
        <Typography sx={{ m: "0 !important" }}>برای زمانبندی مسیر کلیک کنید</Typography>
      </HintBox>
    </>
  );
}
